import { useCallback, useEffect, useRef, useState } from "react";
import { FIELD_NAMES } from "constants/pricerTable";
import {
  PRICER_DATAFunc,
  PRICER_FILTER_BUTTON_SELECTED,
} from "helpers/Pricer/utils";
import {
  ACTIONS,
  CATEGORIES,
  COMPONENT_NAMES,
  CONTEXTS,
  EVENT_NAMES,
  sendEvent,
} from "helpers/googleAnalyticsHelper";
import { FILTER_BUTTONS_NAMES } from "./constants";

/**
 * Commodity (product) filter pills on the Pricer toolbar: the product list,
 * the selected pill (restored from local storage) and the AG Grid filter.
 */
const useCommodityFilter = ({
  pricerTablesData,
  pricerOptInfo,
  pricerRefAgGrid,
  setPricerOperation,
}) => {
  const [productsData, setProductsData] = useState([]);
  const [buttonSelected, setButtonSelected] = useState(null);

  const applySelectedFilter = useCallback(
    (buttonClicked) => {
      const filterInstance =
        pricerRefAgGrid?.api &&
        pricerRefAgGrid?.api.getFilterInstance(FIELD_NAMES.PRODUCT);
      if (buttonClicked !== FILTER_BUTTONS_NAMES.ALL) {
        filterInstance?.setModel({ values: [buttonClicked] });
      }
      if (buttonClicked === FILTER_BUTTONS_NAMES.ALL) {
        filterInstance?.setModel({ values: null });
      }
      pricerRefAgGrid?.api && pricerRefAgGrid.api.onFilterChanged();
    },
    [pricerRefAgGrid]
  );

  const onFilterButtonClick = useCallback(
    (buttonClicked) => {
      setButtonSelected(buttonClicked);
      sendEvent({
        category: CATEGORIES.PRICER,
        context: CONTEXTS.PRICER,
        action: ACTIONS.CLICK,
        component: COMPONENT_NAMES.BUTTON,
        eventName: EVENT_NAMES.PRICER_COMMODITY_FILTER_BUTTON,
        extraParams: { filter: buttonClicked },
      });
      const filterInstance =
        pricerRefAgGrid?.api &&
        pricerRefAgGrid?.api.getFilterInstance(FIELD_NAMES.PRODUCT);
      if (!filterInstance) return;
      setPricerOperation({
        ...pricerOptInfo,
        [PRICER_FILTER_BUTTON_SELECTED]: buttonClicked,
      });
      applySelectedFilter(buttonClicked);
    },
    [pricerRefAgGrid, pricerOptInfo, setPricerOperation, applySelectedFilter]
  );

  // Derived from the already-fetched pricer rows instead of firing a second
  // request against /option-rows just to list distinct products.
  useEffect(() => {
    if (!pricerTablesData || pricerTablesData.length === 0) return;
    const filterButtons = [
      ...new Set(pricerTablesData.map((item) => item[FIELD_NAMES.PRODUCT])),
    ];
    setProductsData([
      FILTER_BUTTONS_NAMES.ALL,
      ...filterButtons.filter((product) => product),
    ]);
  }, [pricerTablesData]);

  const previousSelectedFilter =
    PRICER_DATAFunc()?.[PRICER_FILTER_BUTTON_SELECTED];

  // Only the row count should re-trigger the two effects below, so read the
  // latest filter applier through a ref instead of listing it as a dependency.
  const pricerRowCount = pricerTablesData.length;
  const applySelectedFilterRef = useRef(applySelectedFilter);
  applySelectedFilterRef.current = applySelectedFilter;

  //2 Effect To Store Selected Button And Get Value From Local Storage
  useEffect(() => {
    if (!previousSelectedFilter || pricerRowCount === 0) return;
    setButtonSelected(previousSelectedFilter);
  }, [previousSelectedFilter, pricerRowCount]);

  useEffect(() => {
    if (!buttonSelected || pricerRowCount === 0) return;
    applySelectedFilterRef.current(buttonSelected);
  }, [buttonSelected, pricerRowCount]);

  return { productsData, buttonSelected, onFilterButtonClick };
};

export default useCommodityFilter;
